/* eslint-disable unused-imports/no-unused-vars */
import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { ColorConstants, ProjectGroupPermission, UserType } from "../../../../constants";
import { CursorChangeProps, ResultType, SelectData } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { getAggregateIssuances } from "../../../../service/query/QueryService";
import { ResultData, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { flattenObject } from "../../../../utils";
import { getProjectDetailsByUuid } from "../../../../utils/routes";
import {
  ChartDatasets,
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
  StackedChartData,
} from "../../../../widget";
import { InventoryManagementBoardPageData } from "../../inventory/inventory-management-board/useInventoryManagementBoard";
import { LandholdingContext } from "../LandholdingContext";

interface UseInventoryManagementBoardReturnData {
  gridOnChange: DataGridCursorDataLoadEventHandler;
  isReadableProject: boolean;
  hasData: boolean;
  hasEnoughDataForScrolling: boolean;
  isLoading: boolean;
  dropDownData: SelectData;
  selectedUnitMeasurementData: string;
  handleUpdateSelectedUnitMeasurement: (value: string) => void;
  activeSelectOptionText: string;
  setActiveSelectOptionText: (value: string) => void;
  inventorySelectorButtons: string[];
  pageData: InventoryTabData;
  columns: DataGridColumnDefinition[];
  tagsFilterData: SelectData;
  onTagsFilter: (keys: string[]) => Promise<void>;
  tagsFilterSelectedData?: string;
  currentUserType?: UserType;
}

export interface InventoryTabData {
  chartData: StackedChartData[] | undefined;
  chartDatasets: ChartDatasets | undefined;
  chartLabels: (string | string[])[];
}

export const useInventory = (): UseInventoryManagementBoardReturnData => {
  const { currentUserType } = useAuth();

  const { landholdingDetails, publicLandholdingDetails, readGroupProjectsPermission } = useContext(LandholdingContext);
  const isReadableProject = !landholdingDetails;
  const [dropDownData, setDropDownData] = useState<SelectData>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [hasEnoughDataForScrolling, setHasEnoughDataForScrolling] = useState(false);
  const [selectedUnitMeasurementData, setSelectedUnitMeasurementData] = useState<string>("");
  const [activeSelectOptionText, setActiveSelectOptionText] = useState<string>("By project");
  const [inventorySelectorButtons, setInventorySelectorButtons] = useState<string[]>([]);
  const [tagsFilterSelectedData, setTagsFilterSelectedData] = useState<string | undefined>();
  const [tagsFilterData, setTagsFilterData] = useState<SelectData>([]);
  const location = useLocation();

  const [pageData, setPageData] = useState<InventoryManagementBoardPageData>({
    chartData: [],
    chartLabels: [],
    chartDatasets: [],
  });

  useEffect(() => {
    setActiveSelectOptionText("By project");
  }, [selectedUnitMeasurementData]);

  const handleUpdateSelectedUnitMeasurement = (value: string): void => {
    setSelectedUnitMeasurementData(value);
    setActiveSelectOptionText("By project");
    switch (value) {
      case "Carbon":
        setInventorySelectorButtons(["By project", "By vintage", "By type", "By unit"]);
        break;
      case "Biodiversity":
        setInventorySelectorButtons(["By project", "By habitat group", "By condition"]);
        break;
      case "":
      default:
        setInventorySelectorButtons([]);
    }
  };

  const formatData = useCallback(
    (responseData: ResultData[]): ResultData[] =>
      responseData.map((el) => {
        const result = flattenObject(el);

        if (activeSelectOptionText === "By project") {
          result.link = <DataGridLinkCellFormatterData>{
            text: el.groupByFieldContent1,
            to: getProjectDetailsByUuid(el.groupByFieldContent2?.toString() || "", currentUserType),
            state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
          };
        }
        return result;
      }) || [],
    []
  );

  // Common column definitions
  const groupByName1Column: DataGridColumnDefinition = {
    name: "Name1",
    key: "groupByFieldContent1",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
    minWidth: 140,
  };

  const groupByName2Column: DataGridColumnDefinition = {
    name: "Name2",
    key: "groupByFieldContent2",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  };

  const groupByName3Column: DataGridColumnDefinition = {
    name: "Name3",
    key: "groupByFieldContent3",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  };

  const sumAreaHaColumn: DataGridColumnDefinition = {
    name: "Unit size (Ha)",
    key: "areaHa",
    dataType: "number",
    filterable: true,
    sortable: true,
  };
  const commonColumns: DataGridColumnDefinition[] = isReadableProject
    ? [
        { name: "Available", key: "availableUnits", dataType: "number", filterable: true, sortable: true },
        { name: "Reserved", key: "reservedUnits", dataType: "number", filterable: true, sortable: true },
        { name: "Sold", key: "soldUnits", dataType: "number", filterable: true, sortable: true },
        {
          name: "% sold",
          key: "percentageSold",
          formatter: "percentage",
          dataType: "number",
          filterable: true,
          sortable: true,
        },
      ]
    : [
        { name: "Allocated", key: "allocatedUnits", dataType: "number", filterable: false, sortable: false },
        { name: "Available", key: "availableUnits", dataType: "number", filterable: true, sortable: true },
      ];

  const columnsDict: Record<string, DataGridColumnDefinition[]> = {
    "|projectNameMeasurement": [
      { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
      { ...groupByName3Column, name: "Measurement" },
    ],
    "Carbon|projectName": [{ ...groupByName1Column, key: "link", name: "Name", formatter: "link" }],
    "Carbon|vintageStartDate": [{ ...groupByName1Column, name: "Vintage" }],
    "Carbon|projectType": [{ ...groupByName1Column, name: "Project type" }],
    "Carbon|quantityType": [{ ...groupByName1Column, name: "Quantity type" }],
    "Biodiversity|projectName": [{ ...groupByName1Column, key: "link", name: "Name", formatter: "link" }],
    "Biodiversity|habitat": [
      { ...groupByName1Column, name: "Habitat group" },
      { ...groupByName2Column, name: "Distinctiveness" },
      sumAreaHaColumn,
    ],
    "Biodiversity|habitatCondition": [{ ...groupByName1Column, name: "Habitat condition" }],
  };

  const gridGetColumns = (
    unitMeasurement: string | undefined | null,
    groupCondition: string | undefined | null
  ): DataGridColumnDefinition[] => {
    const dictKey = `${unitMeasurement || ""}|${groupCondition || ""}`;
    return [...(columnsDict[dictKey] || []), ...commonColumns];
  };

  const optionDict: { [key: string]: string } = {
    "By project": selectedUnitMeasurementData === "" ? "projectNameMeasurement" : "projectName",
    "By habitat group": "habitat",
    "By condition": "habitatCondition",
    "By vintage": "vintageStartDate",
    "By type": "projectType",
    "By unit": "quantityType",
  };
  const getGroupCondition = (optionText: string): string => {
    if (optionDict[optionText] === undefined && selectedUnitMeasurementData === "") {
      return selectedUnitMeasurementData === "" ? "projectNameMeasurement" : "projectName";
    }
    return optionDict[optionText];
  };

  const gridOnChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    setIsLoading(true);
    const filterCriteria = dataGridMapFilterCriteria(filtering);
    if (sorting.length === 0) {
      sorting.push({ key: "groupByFieldContent1", direction: "asc" });
    }

    const data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCondition = [];
    if (selectedUnitMeasurementData) {
      filterCondition.push({
        filterBy: "measurementType",
        filterOperator: "eq",
        filterValue: selectedUnitMeasurementData,
      });
    }
    if (tagsFilterSelectedData) {
      filterCondition.push({
        filterBy: "tag",
        filterOperator: "in",
        filterValue: `[${tagsFilterSelectedData}]`,
      });
    }

    filterCondition.push({
      filterBy: "project",
      filterOperator: "in",
      filterValue: `[${landholdingDetails ? landholdingDetails.projects.map((p) => p.uuid) : publicLandholdingDetails?.projects.map((p) => p.uuid)}]`,
    });

    if (filterCriteria.link) {
      filterCriteria.groupByFieldContent1 = filterCriteria.link;
      delete filterCriteria.link;
    }

    const sortKeyMap: Record<string, string> = {
      link: "groupByFieldContent1",
    };

    await getAggregateIssuances({
      filters: filterCondition,
      groupBy: [getGroupCondition(activeSelectOptionText)],
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: paging.pageSize,
      },
      isReadableProject: readGroupProjectsPermission !== ProjectGroupPermission.NONE,
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((s: { key: any; direction: any }) => ({
        key: sortKeyMap[s.key] || s.key,
        direction: s.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        if (response.status === Status.Success && response.data) {
          const dropDownSelectData = response.data.aspects.measurementTypes.map((v: string) => ({
            key: v,
            value: v,
          })) as SelectData;
          dropDownSelectData.unshift({ key: "", value: "Overall" });

          const carbonIdx = dropDownSelectData.findIndex((item) => item.key === "Carbon");
          if (carbonIdx > 1) {
            const [carbonItem] = dropDownSelectData.splice(carbonIdx, 1);
            dropDownSelectData.splice(1, 0, carbonItem);
          }

          setDropDownData(dropDownSelectData);
          const chartLabelsArray: (string | string[])[] = [];
          const gridData: ResultData[] = [];
          response.data.results.forEach((result) => {
            let nameValue: any = result.groupByFieldContent1 ?? "";
            if (nameValue instanceof Date) {
              nameValue = nameValue.getFullYear().toString();
            }

            let chartLabel = nameValue;
            if (activeSelectOptionText === "By habitat group") {
              chartLabel = [result.groupByFieldContent1, result.groupByFieldContent2];
            }

            gridData.push({
              groupByFieldContent1: nameValue,
              groupByFieldContent2: result.groupByFieldContent2,
              groupByFieldContent3: result.groupByFieldContent3,
              availableUnits: Number(result.availableUnits ?? 0),
              reservedUnits: Number(result.reservedUnits ?? 0),
              soldUnits: Number(result.soldUnits ?? 0),
              allocatedUnits: Number(result.reservedUnits ?? 0) + Number(result.soldUnits ?? 0),
              percentageSold: Number(result.percentageSold.toFixed(2) ?? 0),
              averagePrice: Number(result.averagePrice ?? 0),
              areaHa: Number(result.areaHa ?? 0),
            });
            chartLabelsArray.push(chartLabel);
          });
          const chartData: StackedChartData[] = isReadableProject
            ? [
                {
                  label: "Available",
                  data: gridData.map((item) => item.availableUnits as number),
                  backgroundColor:
                    selectedUnitMeasurementData === "Carbon" ? ColorConstants.INDIGO_100 : ColorConstants.GREEN_200,
                  hoverBackgroundColor: selectedUnitMeasurementData === "Carbon" ? "#E7E6F2" : ColorConstants.GREEN_100,
                },
                {
                  label: "Reserved",
                  data: gridData.map((item) => item.reservedUnits as number),
                  backgroundColor:
                    selectedUnitMeasurementData === "Carbon" ? ColorConstants.INDIGO_300 : ColorConstants.GREEN_400,
                  hoverBackgroundColor:
                    selectedUnitMeasurementData === "Carbon" ? ColorConstants.INDIGO_200 : ColorConstants.GREEN_300,
                },
                {
                  label: "Sold",
                  data: gridData.map((item) => item.soldUnits as number),
                  backgroundColor:
                    selectedUnitMeasurementData === "Carbon" ? ColorConstants.INDIGO_500 : ColorConstants.GREEN_600,
                  hoverBackgroundColor:
                    selectedUnitMeasurementData === "Carbon" ? ColorConstants.INDIGO_400 : ColorConstants.GREEN_500,
                },
              ]
            : [
                {
                  label: "Allocated",
                  data: gridData.map((item) => item.allocatedUnits as number),
                  backgroundColor:
                    selectedUnitMeasurementData === "Carbon" ? ColorConstants.INDIGO_500 : ColorConstants.GREEN_600,
                  hoverBackgroundColor:
                    selectedUnitMeasurementData === "Carbon" ? ColorConstants.INDIGO_400 : ColorConstants.GREEN_500,
                },
                {
                  label: "Available",
                  data: gridData.map((item) => item.availableUnits as number),
                  backgroundColor:
                    selectedUnitMeasurementData === "Carbon" ? ColorConstants.INDIGO_100 : ColorConstants.GREEN_200,
                  hoverBackgroundColor: selectedUnitMeasurementData === "Carbon" ? "#E7E6F2" : ColorConstants.GREEN_100,
                },
              ];
          setPageData({
            chartDatasets: [],
            chartData,
            chartLabels: chartLabelsArray,
          });
          data.resultData.push(...formatData(gridData));

          data.paging = {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          };

          setTagsFilterData(
            (response.data.aspects.tags || []).map((v) => ({
              key: `${v.tagUuid}`,
              value: JSON.parse(v.tagData)?.name,
            }))
          );
          setHasData(response.data.results.length > 0);
          setHasEnoughDataForScrolling(response.data.results.length >= 5);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    return data;
  };

  const onTagsFilter = async (keys: string[]): Promise<void> => {
    setTagsFilterSelectedData(keys.length > 0 ? keys.join(",") : undefined);
  };

  return {
    gridOnChange,
    isReadableProject,
    isLoading,
    hasData,
    hasEnoughDataForScrolling,
    dropDownData,
    selectedUnitMeasurementData,
    handleUpdateSelectedUnitMeasurement,
    activeSelectOptionText,
    setActiveSelectOptionText,
    inventorySelectorButtons,
    pageData,
    columns: gridGetColumns(selectedUnitMeasurementData, getGroupCondition(activeSelectOptionText)),
    tagsFilterData,
    onTagsFilter,
    tagsFilterSelectedData,
    currentUserType,
  };
};
